import { useEffect, useMemo, useRef, useState, type DragEvent, type RefObject } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { isDigestInFlight } from "@/lib/types";
import type {
  CourseContentView,
  IngestJobStatus,
  IngestJobView,
  IngestSupportedTypes,
  KnowledgeDigestView,
  KnowledgeSettingsView,
  QuarantinedChunk,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";
import {
  fromFileList,
  planUploads,
  type PickedFile,
  type SkippedFile,
  type SkipReason,
} from "@/lib/uploadPlanner";
import { runQueue, withRetries } from "@/lib/uploadQueue";
import { collectEntries, entriesFromDataTransfer } from "@/lib/fileTree";

const TERMINAL_STATUSES: IngestJobStatus[] = ["SUCCEEDED", "FAILED"];

/**
 * Course dumps arrive as whole folders (hundreds of transcript files), so
 * uploads run through a small concurrent queue: enough parallelism to not be
 * serial over the wire, small enough to leave the browser and the backend's
 * request pool alone. The ingest job queue paces the heavy work server-side.
 */
const UPLOAD_CONCURRENCY = 3;

/**
 * How often to poll a digest whose generation is still outstanding. The run
 * itself takes minutes, so this is about how promptly the page notices it
 * finished, not about racing it.
 */
const DIGEST_POLL_MS = 3000;

type BatchItemStatus = "queued" | "uploading" | "done" | "failed";

interface BatchItem {
  relPath: string;
  file: File;
  status: BatchItemStatus;
  error?: string;
}

export default function KnowledgePage() {
  const queryClient = useQueryClient();
  const filesRef = useRef<HTMLInputElement | null>(null);
  const folderRef = useRef<HTMLInputElement | null>(null);

  const quarantineRef = useRef<HTMLDivElement | null>(null);

  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [topicsInput, setTopicsInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [summarizeUploads, setSummarizeUploads] = useState(true);
  const [digestDraft, setDigestDraft] = useState<string | null>(null);
  const [targetWordsInput, setTargetWordsInput] = useState<string | null>(null);
  const [summaryWordsInput, setSummaryWordsInput] = useState<string | null>(null);
  const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
  const [skipped, setSkipped] = useState<SkippedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // The strategy the running/last batch belongs to — retries must target it
  // even if the operator switches strategies after the batch finished.
  const [batchStrategyId, setBatchStrategyId] = useState<string | null>(null);

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  useEffect(() => {
    if (strategyId) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategyId(first.id);
  }, [strategiesQ.data, strategyId]);

  const quarantineQ = useQuarantine(strategyId);

  const jobsQ = useQuery({
    queryKey: ["ingest-jobs", strategyId],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<IngestJobView[]>(`/api/v1/strategies/${strategyId}/ingest/jobs`),
    refetchInterval: (query) => {
      const data = query.state.data as IngestJobView[] | undefined;
      const hasInflight = data?.some((j) => !TERMINAL_STATUSES.includes(j.status));
      return hasInflight ? 2_000 : false;
    },
  });

  const contentQ = useIngestContent(strategyId);

  const supportedQ = useQuery({
    queryKey: ["ingest-supported-types", strategyId],
    enabled: !!strategyId,
    staleTime: Infinity,
    queryFn: () =>
      api.get<IngestSupportedTypes>(
        `/api/v1/strategies/${strategyId}/ingest/supported-types`,
      ),
  });

  const settingsQ = useQuery({
    queryKey: ["knowledge-settings", strategyId],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<KnowledgeSettingsView>(
        `/api/v1/strategies/${strategyId}/knowledge-settings`,
      ),
  });

  const settingsSaveM = useMutation({
    mutationFn: (summaryTargetWords: number) =>
      api.put<KnowledgeSettingsView>(
        `/api/v1/strategies/${strategyId}/knowledge-settings`,
        { summaryTargetWords },
      ),
    onSuccess: (view) => {
      queryClient.setQueryData(["knowledge-settings", strategyId], view);
      setSummaryWordsInput(null);
    },
  });

  // Generation runs as a queued job: the POST returns 202 immediately and the
  // work takes minutes, so progress arrives by polling this view rather than by
  // holding a request open (which the ingress would time out at 60s anyway).
  const digestQ = useQuery({
    queryKey: ["knowledge-digest", strategyId],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<KnowledgeDigestView>(`/api/v1/strategies/${strategyId}/knowledge-digest`),
    refetchInterval: (q) =>
      isDigestInFlight(q.state.data?.status) ? DIGEST_POLL_MS : false,
  });

  const digestGenerateM = useMutation({
    mutationFn: (targetWords: number | null) =>
      api.post<KnowledgeDigestView>(
        `/api/v1/strategies/${strategyId}/knowledge-digest/generate` +
          (targetWords ? `?targetWords=${targetWords}` : ""),
      ),
    // The response is the queued (PENDING) digest, not the finished one —
    // seeding it here is what starts digestQ polling for the result.
    onSuccess: (view) => {
      queryClient.setQueryData(["knowledge-digest", strategyId], view);
      setDigestDraft(null);
      setTargetWordsInput(null);
    },
  });

  // Cover both the moment before the 202 lands and the minutes of queued work
  // after it, so the button never looks idle while a run is outstanding.
  const digestGenerating =
    digestGenerateM.isPending || isDigestInFlight(digestQ.data?.status);

  const digestSaveM = useMutation({
    mutationFn: (digest: string) =>
      api.put<KnowledgeDigestView>(`/api/v1/strategies/${strategyId}/knowledge-digest`, {
        digest,
      }),
    onSuccess: (view) => {
      queryClient.setQueryData(["knowledge-digest", strategyId], view);
      setDigestDraft(null);
    },
  });

  function patchItem(relPath: string, patch: Partial<BatchItem>) {
    setBatchItems((prev) =>
      prev.map((it) => (it.relPath === relPath ? { ...it, ...patch } : it)),
    );
  }

  async function uploadItems(sid: string, items: BatchItem[]) {
    setIsUploading(true);
    const topics = topicsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    // A hand-typed label only makes sense for a single file; folder uploads
    // label each job with the file's path so provenance survives the batch.
    const singleLabel = items.length === 1 && labelInput.trim() ? labelInput.trim() : null;
    let jobsVisible = false;

    await runQueue(
      items,
      async (item) => {
        patchItem(item.relPath, { status: "uploading", error: undefined });
        // The ingress rate-limits the API per minute; a folder upload is
        // expected to overrun it. Back off and settle to the server's pace
        // instead of failing the file.
        return withRetries(
          () => {
            const fd = new FormData();
            fd.append("file", item.file, item.file.name);
            fd.append("label", singleLabel ?? item.relPath);
            fd.append("summarize", String(summarizeUploads));
            topics.forEach((t) => fd.append("topics", t));
            return api.upload<IngestJobView>(`/api/v1/strategies/${sid}/ingest`, fd);
          },
          {
            attempts: 10,
            baseDelayMs: 2_000,
            maxDelayMs: 20_000,
            isRetryable: (e) =>
              e instanceof ApiError && [429, 502, 503, 504].includes(e.status),
          },
        );
      },
      {
        concurrency: UPLOAD_CONCURRENCY,
        onSettled: (index, result) => {
          // In-bounds: runQueue settles indices of the array it was given.
          const { relPath } = items[index]!;
          if (result.ok) {
            patchItem(relPath, { status: "done" });
            if (!jobsVisible) {
              // One refresh so the jobs table starts showing the batch; its
              // own 2s inflight polling keeps it current from here on.
              jobsVisible = true;
              queryClient.invalidateQueries({ queryKey: ["ingest-jobs", sid] });
            }
          } else {
            patchItem(relPath, { status: "failed", error: errorMessage(result.error) });
          }
        },
      },
    );

    setIsUploading(false);
    queryClient.invalidateQueries({ queryKey: ["ingest-jobs", sid] });
    queryClient.invalidateQueries({ queryKey: ["ingest-content", sid] });
  }

  async function startBatch(picked: PickedFile[]) {
    if (!strategyId || !supportedQ.data || isUploading || picked.length === 0) return;
    const plan = planUploads(picked, supportedQ.data);
    setSkipped(plan.skipped);
    setUploadError(
      plan.accepted.length === 0
        ? "Nothing to upload — every file in the selection was skipped."
        : null,
    );
    const items: BatchItem[] = plan.accepted.map((p) => ({
      relPath: p.relPath,
      file: p.file,
      status: "queued",
    }));
    setBatchItems(items);
    setBatchStrategyId(strategyId);
    if (items.length > 0) await uploadItems(strategyId, items);
  }

  async function retryFailed() {
    if (!batchStrategyId || isUploading) return;
    const failed = batchItems.filter((it) => it.status === "failed");
    if (failed.length > 0) await uploadItems(batchStrategyId, failed);
  }

  const deleteM = useMutation({
    mutationFn: async (contentId: string) => {
      if (!strategyId) throw new Error("no strategy selected");
      await api.del<void>(`/api/v1/strategies/${strategyId}/ingest/content/${contentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ingest-content", strategyId] });
      queryClient.invalidateQueries({ queryKey: ["ingest-jobs", strategyId] });
    },
  });

  const inflightCount = useMemo(
    () => jobsQ.data?.filter((j) => !TERMINAL_STATUSES.includes(j.status)).length ?? 0,
    [jobsQ.data],
  );

  function onPickedFiles(list: FileList | null) {
    if (!list || list.length === 0) return;
    void startBatch(fromFileList(list));
  }

  async function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (isUploading) return;
    const entries = entriesFromDataTransfer(e.dataTransfer);
    const picked = entries ? await collectEntries(entries) : fromFileList(e.dataTransfer.files);
    void startBatch(picked);
  }

  const doneCount = batchItems.filter((it) => it.status === "done").length;
  const failedItems = batchItems.filter((it) => it.status === "failed");
  const acceptAttr = supportedQ.data?.extensions.join(",");
  const pickersDisabled = !strategyId || !supportedQ.data || isUploading;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Knowledge base</h1>
        <p className="text-sm text-[var(--fg-muted)]">
          Upload course material — PDF, DOCX, audio, video. Text is extracted, chunked, and embedded
          for retrieval at email-generation time.
        </p>
      </header>

      <QuarantineNotice
        count={quarantineQ.data?.length ?? 0}
        onReview={() => {
          const region = quarantineRef.current;
          if (!region) return;
          region.scrollIntoView({ behavior: "smooth", block: "start" });
          // Scrolling alone leaves keyboard and screen-reader users where they
          // were, so the caret follows the viewport.
          region.focus({ preventScroll: true });
        }}
      />

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Strategy</div>
          <select
            value={strategyId ?? ""}
            onChange={(e) => setStrategyId(e.target.value || null)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white min-w-[16rem]"
            disabled={strategiesQ.isLoading || !strategiesQ.data?.length || isUploading}
          >
            {strategiesQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.slug})
              </option>
            ))}
            {!strategiesQ.data?.length ? <option value="">No strategies</option> : null}
          </select>
        </label>
        <div className="text-xs text-[var(--fg-muted)] pb-1.5">
          {inflightCount > 0
            ? `${inflightCount} job${inflightCount === 1 ? "" : "s"} in progress`
            : "Idle"}
        </div>
      </div>

      <Card>
        <CardTitle>Upload new content</CardTitle>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm flex-1 min-w-[16rem]">
            <div className="text-xs text-[var(--fg-muted)] mb-1">
              Label (optional — single-file uploads; folder uploads use each file's path)
            </div>
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              placeholder="e.g. Module 3 — Discovery"
              className="w-full rounded-md border border-[var(--border)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <label className="text-sm flex-1 min-w-[16rem]">
            <div className="text-xs text-[var(--fg-muted)] mb-1">
              Topics (comma-separated, used as RAG filter)
            </div>
            <input
              type="text"
              value={topicsInput}
              onChange={(e) => setTopicsInput(e.target.value)}
              placeholder="discovery, objections"
              className="w-full rounded-md border border-[var(--border)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
        </div>
        <div
          className="mt-3 rounded-md border border-dashed border-[var(--border)] p-4"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          data-testid="upload-dropzone"
        >
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={filesRef}
              type="file"
              multiple
              hidden
              accept={acceptAttr}
              onChange={(e) => {
                onPickedFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={folderRef}
              type="file"
              hidden
              {...({ webkitdirectory: "" } as object)}
              onChange={(e) => {
                onPickedFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={pickersDisabled}
              onClick={() => filesRef.current?.click()}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 disabled:opacity-50"
            >
              Choose files…
            </button>
            <button
              type="button"
              disabled={pickersDisabled}
              onClick={() => folderRef.current?.click()}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 disabled:opacity-50"
            >
              Choose folder…
            </button>
            <span className="text-xs text-[var(--fg-muted)]">
              …or drop files / a folder here. Subfolders are scanned; unsupported files are
              skipped and listed below.
            </span>
            <label className="flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
              <input
                type="checkbox"
                checked={summarizeUploads}
                onChange={(e) => setSummarizeUploads(e.target.checked)}
                disabled={isUploading}
              />
              Generate per-file summary (LLM)
            </label>
          </div>

          {summarizeUploads ? (
            <div className="mt-3 rounded-md bg-[var(--bg-subtle,#f6f7f9)] border border-[var(--border)] p-3 text-xs text-[var(--fg-muted)] space-y-2">
              <p>
                <strong className="text-[var(--fg)]">Decide before you upload:</strong> each
                file gets one LLM-written summary, on top of extraction and embedding. A
                summary does not change what retrieval finds — chunks are searched either
                way — but the knowledge digest below is distilled from summaries (and from
                a plain excerpt where a file has none), so summary length sets the ceiling
                on how much technique detail the agent's standing brief can carry.
              </p>
              <p>
                Summaries are written{" "}
                <strong className="text-[var(--fg)]">once, at ingest</strong>: raising this
                later applies only to new uploads — existing files keep the summary they
                were ingested with unless you re-ingest them. Longer summaries cost
                proportionally more LLM spend per file. A summary that fails does not fail
                the upload.
              </p>
              <label className="flex items-center gap-1.5">
                Summary length
                <input
                  type="number"
                  min={settingsQ.data?.minSummaryTargetWords ?? 80}
                  max={settingsQ.data?.maxSummaryTargetWords ?? 2000}
                  step={50}
                  aria-label="Per-file summary length in words"
                  value={summaryWordsInput ?? settingsQ.data?.summaryTargetWords ?? 600}
                  onChange={(e) => setSummaryWordsInput(e.target.value)}
                  disabled={!settingsQ.data || isUploading || settingsSaveM.isPending}
                  className="w-24 rounded-md border border-[var(--border)] px-2 py-1 text-sm"
                />
                words per file
                {summaryWordsInput !== null &&
                Number(summaryWordsInput) !== settingsQ.data?.summaryTargetWords ? (
                  <button
                    type="button"
                    onClick={() => settingsSaveM.mutate(Number(summaryWordsInput))}
                    disabled={settingsSaveM.isPending}
                    className="rounded-md border border-[var(--border)] px-2 py-1 hover:bg-black/5 disabled:opacity-50"
                  >
                    {settingsSaveM.isPending ? "Saving…" : "Save"}
                  </button>
                ) : null}
              </label>
            </div>
          ) : null}

          {batchItems.length > 0 ? (
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <span>
                  Uploaded {doneCount}/{batchItems.length}
                  {failedItems.length > 0 ? (
                    <span className="text-[var(--danger)]"> · {failedItems.length} failed</span>
                  ) : null}
                </span>
                {isUploading ? (
                  <span className="text-[var(--fg-muted)]">Uploading…</span>
                ) : null}
                {!isUploading && failedItems.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => void retryFailed()}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Retry failed
                  </button>
                ) : null}
              </div>
              {failedItems.length > 0 ? (
                <ul className="max-h-40 overflow-y-auto text-xs text-[var(--danger)] space-y-0.5">
                  {failedItems.map((it) => (
                    <li key={it.relPath}>
                      {it.relPath}
                      {it.error ? ` — ${it.error}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {skipped.length > 0 ? (
            <div
              className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900"
              data-testid="skipped-files"
            >
              <div className="font-medium">
                {skipped.length} file{skipped.length === 1 ? "" : "s"} skipped — not uploaded
              </div>
              <ul className="mt-1 max-h-40 overflow-y-auto text-xs space-y-0.5">
                {skipped.map((s) => (
                  <li key={s.relPath}>
                    {s.relPath} — {skipReasonLabel(s.reason, supportedQ.data?.maxFileSizeBytes)}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {uploadError ? (
            <div className="mt-2 text-sm text-[var(--danger)]">{uploadError}</div>
          ) : null}
        </div>
      </Card>

      <Card>
        <CardTitle>Knowledge digest</CardTitle>
        <p className="text-xs text-[var(--fg-muted)] mb-2">
          A standing method brief distilled from this strategy's ingested material. When
          present, it is included in every generated email's system prompt (and cached), on
          top of per-email retrieval. Longer digests carry more technique detail but add
          their tokens to every email prompt (largely offset by prompt caching). Review
          before saving — this text reaches the agent verbatim.
        </p>
        {!digestQ.data ? (
          <div className="text-sm text-[var(--fg-muted)]">Loading…</div>
        ) : (
          <div className="space-y-2">
            <textarea
              value={digestDraft ?? digestQ.data?.digest ?? ""}
              onChange={(e) => setDigestDraft(e.target.value)}
              placeholder="No digest yet — generate one from the ingested content, or write your own."
              rows={8}
              className="w-full rounded-md border border-[var(--border)] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-[var(--fg-muted)]">
                Length
                <input
                  type="number"
                  min={digestQ.data?.minTargetWords ?? 300}
                  max={digestQ.data?.maxTargetWords ?? 8000}
                  step={100}
                  aria-label="Knowledge digest length in words"
                  value={targetWordsInput ?? digestQ.data?.targetWords ?? 2500}
                  onChange={(e) => setTargetWordsInput(e.target.value)}
                  disabled={digestGenerating}
                  className="w-24 rounded-md border border-[var(--border)] px-2 py-1 text-sm"
                />
                words
              </label>
              <button
                type="button"
                onClick={() =>
                  digestGenerateM.mutate(
                    targetWordsInput ? Number(targetWordsInput) : null,
                  )
                }
                disabled={!strategyId || digestGenerating}
                className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 disabled:opacity-50"
              >
                {digestGenerating
                  ? "Generating…"
                  : digestQ.data?.digest
                    ? "Regenerate from content"
                    : "Generate from content"}
              </button>
              {digestDraft !== null && digestDraft !== (digestQ.data?.digest ?? "") ? (
                <button
                  type="button"
                  onClick={() => digestSaveM.mutate(digestDraft)}
                  disabled={digestSaveM.isPending}
                  className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {digestSaveM.isPending ? "Saving…" : "Save edit"}
                </button>
              ) : null}
              {digestQ.data?.updatedAt ? (
                <span className="text-xs text-[var(--fg-muted)]">
                  Updated {formatDateTime(digestQ.data.updatedAt)}
                  {digestQ.data.model ? ` · ${digestQ.data.model}` : ""}
                </span>
              ) : null}
              {digestGenerating ? (
                <span className="text-xs text-[var(--fg-muted)]">
                  Distilling the ingested material — this takes a few minutes. You
                  can leave this page; the run continues on the server.
                </span>
              ) : null}
              {/* A rejected request (no content, no API key) reports here; a run
                  that failed after being accepted reports on the digest itself. */}
              {digestGenerateM.isError ? (
                <span className="text-xs text-[var(--danger)]">
                  {digestGenerateM.error instanceof Error
                    ? digestGenerateM.error.message
                    : "Generation failed"}
                </span>
              ) : digestQ.data?.status === "FAILED" ? (
                <span className="text-xs text-[var(--danger)]">
                  {digestQ.data.error ?? "Generation failed"}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </Card>

      <Card>
        <CardTitle>Ingestion jobs</CardTitle>
        {jobsQ.isLoading ? (
          <div className="text-sm text-[var(--fg-muted)]">Loading…</div>
        ) : !jobsQ.data || jobsQ.data.length === 0 ? (
          <div className="text-sm text-[var(--fg-muted)]">No ingest jobs yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--fg-muted)] border-b border-[var(--border)]">
                <tr>
                  <th className="py-2">Source</th>
                  <th className="py-2">Kind</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Chunks</th>
                  <th className="py-2">Tokens</th>
                  <th className="py-2">Created</th>
                  <th className="py-2">Last error</th>
                </tr>
              </thead>
              <tbody>
                {jobsQ.data.map((j) => (
                  <tr key={j.id} className="border-b border-[var(--border)] last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div>{j.sourceLabel}</div>
                      {j.topics.length > 0 ? (
                        <div className="text-xs text-[var(--fg-muted)]">
                          [{j.topics.join(", ")}]
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-[var(--fg-muted)]">{j.sourceKind}</td>
                    <td className="py-2 pr-3">
                      <StatusPill status={j.status} />
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{j.chunkCount}</td>
                    <td className="py-2 pr-3 tabular-nums">{j.tokenCount}</td>
                    <td className="py-2 pr-3 text-[var(--fg-muted)]">
                      {formatDateTime(j.createdAt)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-[var(--danger)] max-w-md truncate">
                      {j.lastError ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardTitle>Ingested content</CardTitle>
        {contentQ.isLoading ? (
          <div className="text-sm text-[var(--fg-muted)]">Loading…</div>
        ) : !contentQ.data || contentQ.data.length === 0 ? (
          <div className="text-sm text-[var(--fg-muted)]">
            No knowledge has been ingested for this strategy yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--fg-muted)] border-b border-[var(--border)]">
                <tr>
                  <th className="py-2">Source</th>
                  <th className="py-2">Kind</th>
                  <th className="py-2">Chunks</th>
                  <th className="py-2">Ingested</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {contentQ.data.map((c) => (
                  <tr key={c.id} className="border-b border-[var(--border)] last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div>{c.sourceLabel}</div>
                      {/* 1.16 — the summary is what makes a long document
                          reviewable at a glance, so it belongs inline rather
                          than behind another click. */}
                      {c.summary ? (
                        <details className="mt-1">
                          <summary className="text-xs text-[var(--fg-muted)] cursor-pointer hover:underline">
                            Summary
                          </summary>
                          <p className="mt-1 max-w-prose whitespace-pre-wrap text-xs text-[var(--fg-muted)]">
                            {c.summary}
                          </p>
                          {c.summaryModel ? (
                            <p className="mt-1 text-[10px] text-[var(--fg-muted)]">
                              via <code>{c.summaryModel}</code>
                            </p>
                          ) : null}
                        </details>
                      ) : (
                        <div className="mt-1 text-xs text-[var(--fg-muted)]">No summary</div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[var(--fg-muted)]">{c.sourceKind}</td>
                    <td className="py-2 pr-3 tabular-nums">{c.chunkCount}</td>
                    <td className="py-2 pr-3 text-[var(--fg-muted)]">
                      {formatDateTime(c.ingestedAt)}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${c.sourceLabel}?`)) deleteM.mutate(c.id);
                        }}
                        disabled={deleteM.isPending}
                        className="text-xs text-[var(--danger)] hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {strategyId ? (
        <QuarantineCard strategyId={strategyId} regionRef={quarantineRef} />
      ) : null}
    </div>
  );
}

/**
 * The ingested documents. Shared between the corpus table and the quarantine
 * list, which needs it to name the document a held-back passage came from.
 */
function useIngestContent(strategyId: string | null) {
  return useQuery({
    queryKey: ["ingest-content", strategyId],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<CourseContentView[]>(`/api/v1/strategies/${strategyId}/ingest/content`),
  });
}

/**
 * Shared by the notice at the top of the page and the list at the bottom, so
 * the count and the rows are one cached answer rather than two fetches that
 * can disagree with each other.
 */
function useQuarantine(strategyId: string | null) {
  return useQuery({
    queryKey: ["ingest-quarantine", strategyId],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<QuarantinedChunk[]>(`/api/v1/strategies/${strategyId}/ingest/quarantine`),
  });
}

/**
 * Held-back passages are the only thing on this page that waits on a person,
 * and they are listed last — below the uploader, the job table, the digest and
 * a corpus that can run to hundreds of files. On a large knowledge base that is
 * several screens down, i.e. invisible on arrival. The count rides at the top
 * where the page is entered; the list stays at the bottom because it is
 * unbounded and would otherwise bury the page's primary job, uploading.
 */
function QuarantineNotice({ count, onReview }: { count: number; onReview: () => void }) {
  if (count === 0) return null;

  return (
    <div
      role="status"
      data-testid="quarantine-notice"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        <span className="font-semibold">
          {count} passage{count === 1 ? "" : "s"} held for review.
        </span>{" "}
        The injection screen flagged {count === 1 ? "it" : "them"} as possible instructions
        aimed at the agent. {count === 1 ? "It is" : "They are"} excluded from retrieval
        until you release or delete {count === 1 ? "it" : "each one"}.
      </span>
      <button
        type="button"
        onClick={onReview}
        className="ml-auto rounded border border-amber-400 bg-[var(--bg-card)] px-2.5 py-1 text-xs font-medium hover:bg-amber-100"
      >
        Review {count === 1 ? "it" : "them"}
      </button>
    </div>
  );
}

/**
 * Chunks the ingest injection screen held back (heuristics or the LLM pass).
 * They are stored and embedded but excluded from retrieval until a human
 * releases them — or deletes them for good.
 */
function QuarantineCard({
  strategyId,
  regionRef,
}: {
  strategyId: string;
  regionRef: RefObject<HTMLDivElement | null>;
}) {
  const queryClient = useQueryClient();
  const listQ = useQuarantine(strategyId);
  const contentQ = useIngestContent(strategyId);
  // Judging a passage means reading all of it, so every row can be opened;
  // several at once, since reviewing is a comparison across passages.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const labelOf = (contentId: string) =>
    contentQ.data?.find((c) => c.id === contentId)?.sourceLabel ?? null;
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["ingest-quarantine", strategyId] });
  const releaseM = useMutation({
    mutationFn: (chunkId: string) =>
      api.post<void>(`/api/v1/strategies/${strategyId}/ingest/quarantine/${chunkId}/release`),
    onSuccess: invalidate,
  });
  const deleteM = useMutation({
    mutationFn: (chunkId: string) =>
      api.del<void>(`/api/v1/strategies/${strategyId}/ingest/quarantine/${chunkId}`),
    onSuccess: invalidate,
  });

  const rows = listQ.data ?? [];
  if (!listQ.isLoading && rows.length === 0) return null;

  return (
    // tabIndex -1 makes this a focus target for the notice above without
    // putting it in the tab order; scroll-mt keeps the heading clear of the
    // viewport edge after the jump.
    <div
      ref={regionRef}
      tabIndex={-1}
      data-testid="quarantine-region"
      className="scroll-mt-4 outline-none"
    >
      <Card>
        <CardTitle>Quarantined passages</CardTitle>
        <p className="mt-1 text-xs text-[var(--fg-muted)]">
          The injection screen flagged these passages as possible instructions aimed at the
          AI rather than course content. They are <em>not</em> used by the agent until you
          release them. Release a false positive; delete anything that really is an
          instruction payload.
        </p>
        {listQ.isLoading ? (
          <p className="mt-2 text-sm text-zinc-500">Loading…</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {rows.map((c) => (
              <li key={c.id} className="rounded border border-rose-200 bg-rose-50/40 p-2 text-xs">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium text-rose-800">{c.reason ?? "flagged"}</span>
                    <span className="text-[var(--fg-muted)]">
                      {labelOf(c.contentId) ?? "unknown document"} · passage {c.chunkIndex + 1}
                    </span>
                  </span>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      disabled={releaseM.isPending || deleteM.isPending}
                      onClick={() => releaseM.mutate(c.id)}
                      className="rounded border border-[var(--border)] bg-[var(--bg-card)] px-2 py-0.5 hover:bg-zinc-50 disabled:opacity-50"
                    >
                      Release
                    </button>
                    <button
                      type="button"
                      disabled={releaseM.isPending || deleteM.isPending}
                      onClick={() => {
                        if (window.confirm("Delete this passage permanently?")) {
                          deleteM.mutate(c.id);
                        }
                      }}
                      className="rounded border border-rose-300 bg-[var(--bg-card)] px-2 py-0.5 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </span>
                </div>
                <p
                  id={`quarantine-text-${c.id}`}
                  data-testid={`quarantine-text-${c.id}`}
                  className={`whitespace-pre-wrap break-words text-zinc-700 ${
                    expanded.has(c.id) ? "" : "line-clamp-4"
                  }`}
                >
                  {c.text}
                </p>
                <button
                  type="button"
                  aria-expanded={expanded.has(c.id)}
                  aria-controls={`quarantine-text-${c.id}`}
                  onClick={() => toggle(c.id)}
                  className="mt-1 text-rose-700 underline underline-offset-2 hover:text-rose-900"
                >
                  {expanded.has(c.id) ? "Collapse passage" : "Show full passage"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ status }: { status: IngestJobStatus }) {
  const tone =
    status === "SUCCEEDED"
      ? "bg-emerald-100 text-emerald-700"
      : status === "FAILED"
        ? "bg-rose-100 text-rose-700"
        : "bg-blue-100 text-blue-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {status}
    </span>
  );
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | null;
    if (body && typeof body.message === "string") return body.message;
    return e.message;
  }
  return e instanceof Error ? e.message : "Upload failed";
}

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${Math.round(n / (1024 * 1024))} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function skipReasonLabel(reason: SkipReason, maxFileSizeBytes: number | undefined): string {
  switch (reason) {
    case "unsupported":
      return "unsupported file type";
    case "too-large":
      return maxFileSizeBytes
        ? `over the ${formatBytes(maxFileSizeBytes)} limit`
        : "over the size limit";
    case "empty":
      return "empty file";
    case "junk":
      return "system file";
  }
}
